import { motion } from "framer-motion";
import { useState } from "react";
import { Phone, MessageCircle, Clock, MapPin, ArrowUpRight, ExternalLink } from "lucide-react";
import { CLINIC } from "@/lib/assets";
import { toast, Toaster } from "sonner";

const ease = [0.76, 0, 0.24, 1];

export const Consult = () => {
    const [form, setForm] = useState({ name: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please share your name and phone.");
            return;
        }
        setSubmitting(true);
        const text = `New appointment request%0AName: ${encodeURIComponent(
            form.name
        )}%0APhone: ${encodeURIComponent(form.phone)}%0AMessage: ${encodeURIComponent(
            form.message || "-"
        )}`;
        window.open(`${CLINIC.whatsapp}?text=${text}`, "_blank", "noopener,noreferrer");
        toast.success("Opening WhatsApp to send your request.");
        setForm({ name: "", phone: "", message: "" });
        setSubmitting(false);
    };

    return (
        <section
            id="consult"
            className="py-20 md:py-32 lg:py-40 px-5 md:px-8 lg:px-10 relative bg-surya-surface text-surya-ink overflow-hidden"
            data-testid="consult-section"
        >
            <Toaster
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "#FFFFFF",
                        color: "#3D2E25",
                        border: "1px solid #E5E1D8",
                        borderRadius: 0,
                        fontFamily: "Manrope, sans-serif",
                    },
                }}
            />

            <div className="mx-auto max-w-[1600px]">
                <div className="grid grid-cols-12 gap-6 md:gap-8 lg:gap-10 mb-10 md:mb-16 lg:mb-20">
                    <div className="col-span-12 md:col-span-2">
                        <div className="label text-surya-orange">Chapter</div>
                        <div className="chapter-num text-[3rem] md:text-[4rem] lg:text-[4.5rem] leading-none mt-1">
                            05<span className="text-surya-orange">.</span>
                        </div>
                        <div className="label text-surya-muted">The Consult</div>
                    </div>
                    <div className="col-span-12 md:col-span-10">
                        <h2 className="font-serif text-[clamp(2.2rem,6.5vw,6rem)] leading-[0.9] tracking-[-0.03em] font-light">
                            Say hello.{" "}
                            <span className="italic-display text-surya-orange">
                                Book a chair.
                            </span>
                        </h2>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-6 md:gap-8 lg:gap-10">
                    {/* Left rails */}
                    <div className="col-span-12 md:col-span-5 space-y-7 md:space-y-9">
                        <motion.a
                            href={`tel:${CLINIC.phoneE164}`}
                            data-testid="contact-phone"
                            whileHover={{ x: 6 }}
                            transition={{ ease, duration: 0.4 }}
                            className="group flex items-center justify-between border-b border-surya-line pb-5 md:pb-6 hover:border-surya-orange"
                        >
                            <div>
                                <div className="label text-surya-muted mb-2">
                                    <Phone className="inline-block h-3 w-3 mr-2" strokeWidth={1.2} />
                                    Call
                                </div>
                                <div className="font-serif text-2xl md:text-3xl lg:text-4xl font-light">
                                    {CLINIC.phone}
                                </div>
                            </div>
                            <ArrowUpRight className="h-5 w-5 text-surya-ink group-hover:text-surya-orange" strokeWidth={1.2} />
                        </motion.a>

                        <motion.a
                            href={CLINIC.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp"
                            whileHover={{ x: 6 }}
                            transition={{ ease, duration: 0.4 }}
                            className="group flex items-center justify-between border-b border-surya-line pb-5 md:pb-6 hover:border-surya-orange"
                        >
                            <div>
                                <div className="label text-surya-muted mb-2">
                                    <MessageCircle className="inline-block h-3 w-3 mr-2" strokeWidth={1.2} />
                                    WhatsApp
                                </div>
                                <div className="font-serif text-2xl md:text-3xl lg:text-4xl font-light">
                                    <span className="italic-display text-surya-orange">Chat</span>{" "}
                                    with us
                                </div>
                            </div>
                            <ArrowUpRight className="h-5 w-5 text-surya-ink group-hover:text-surya-orange" strokeWidth={1.2} />
                        </motion.a>

                        <div>
                            <div className="label text-surya-muted mb-3">
                                <Clock className="inline-block h-3 w-3 mr-2" strokeWidth={1.2} />
                                Hours
                            </div>
                            <p className="font-serif text-lg md:text-xl lg:text-2xl font-light leading-snug">
                                {CLINIC.hours.weekdays}
                            </p>
                            <p className="font-serif text-lg md:text-xl lg:text-2xl font-light leading-snug italic-display text-surya-orange mt-1">
                                {CLINIC.hours.sunday}
                            </p>
                        </div>

                        <a
                            href={CLINIC.mapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-address"
                            className="group block"
                        >
                            <div className="label text-surya-muted mb-3 flex items-center gap-2">
                                <MapPin className="h-3 w-3" strokeWidth={1.2} />
                                Where
                                <ExternalLink className="h-3 w-3 opacity-60 group-hover:opacity-100" strokeWidth={1.2} />
                            </div>
                            <p className="font-serif text-lg md:text-xl lg:text-2xl font-light leading-snug group-hover:text-surya-orange transition-colors">
                                {CLINIC.address}
                            </p>
                            <p className="text-surya-muted text-xs md:text-sm mt-1">
                                {CLINIC.addressLine2}
                            </p>
                        </a>
                    </div>

                    {/* Right — form */}
                    <div className="col-span-12 md:col-span-6 md:col-start-7">
                        <div className="label text-surya-muted mb-5 md:mb-8">
                            (or leave a note — we&apos;ll call you back)
                        </div>
                        <form onSubmit={submit} data-testid="consult-form" className="space-y-6 md:space-y-8">
                            <Field
                                id="name"
                                label="Your Name"
                                value={form.name}
                                onChange={(v) => setForm({ ...form, name: v })}
                                placeholder="Full name"
                            />
                            <Field
                                id="phone"
                                label="Phone Number"
                                value={form.phone}
                                onChange={(v) => setForm({ ...form, phone: v })}
                                placeholder="10-digit mobile"
                                type="tel"
                            />
                            <Field
                                id="message"
                                label="What brings you in?"
                                value={form.message}
                                onChange={(v) => setForm({ ...form, message: v })}
                                placeholder="e.g. Gum pain, implant enquiry, second opinion…"
                                multiline
                            />

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="consult-submit"
                                className="group relative inline-flex items-center gap-3 border border-surya-orange bg-surya-orange text-white pl-6 pr-6 py-4 label hover:bg-surya-orangeHover disabled:opacity-60"
                            >
                                {submitting ? "Sending…" : "Request Appointment"}
                                <ArrowUpRight
                                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                                    strokeWidth={1.5}
                                />
                            </button>
                        </form>
                    </div>
                </div>

                {/* Map embed */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 1, ease }}
                    className="mt-14 md:mt-20 lg:mt-24 grid grid-cols-12 gap-6 md:gap-8 lg:gap-10 items-end"
                    data-testid="consult-map"
                >
                    <div className="col-span-12 md:col-span-4">
                        <div className="label text-surya-orange mb-3">Find us</div>
                        <h3 className="font-serif text-2xl md:text-4xl lg:text-5xl font-light leading-[0.95] text-surya-ink">
                            Dibdih, opposite{" "}
                            <span className="italic-display">Tata Motors.</span>
                        </h3>
                        <p className="mt-4 md:mt-6 text-surya-inkSoft text-sm md:text-base leading-relaxed">
                            Landmark: Tirkey Complex, just off the main road.
                            Plus code —{" "}
                            <span className="text-surya-ink">{CLINIC.plusCode}</span>.
                        </p>
                        <a
                            href={CLINIC.mapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="consult-directions"
                            className="mt-5 md:mt-8 inline-flex items-center gap-2 label text-surya-ink border-b border-surya-ink hover:text-surya-orange hover:border-surya-orange pb-1"
                        >
                            Get Directions
                            <ArrowUpRight className="h-4 w-4" strokeWidth={1.4} />
                        </a>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <div className="relative aspect-[16/10] md:aspect-[16/9] border border-surya-line overflow-hidden bg-surya-bg">
                            <iframe
                                title="Surya Dental Care Location"
                                src={CLINIC.mapsEmbed}
                                width="100%"
                                height="100%"
                                style={{ border: 0, filter: "contrast(1.02) saturate(0.9)" }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen=""
                            />
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

const Field = ({ id, label, value, onChange, placeholder, type = "text", multiline }) => (
    <div className="relative">
        <label htmlFor={id} className="label text-surya-muted block mb-2 md:mb-3">
            {label}
        </label>
        {multiline ? (
            <textarea
                id={id}
                name={id}
                rows={3}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={`field-${id}`}
                className="w-full bg-transparent border-b border-surya-lineStrong focus:border-surya-orange py-3 font-serif text-lg md:text-xl lg:text-2xl font-light text-surya-ink placeholder:text-surya-muted/60 outline-none resize-none"
            />
        ) : (
            <input
                id={id}
                name={id}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={`field-${id}`}
                className="w-full bg-transparent border-b border-surya-lineStrong focus:border-surya-orange py-3 font-serif text-lg md:text-xl lg:text-2xl font-light text-surya-ink placeholder:text-surya-muted/60 outline-none"
            />
        )}
    </div>
);
